import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import '../../styles/Dashboard.css';
import '../../styles/Forms.css';

const EmployeeProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState({
    myAssets: 0,
    pendingAcknowledgements: 0,
    acknowledgedAssets: 0
  });
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    employeeId: '',
    title: '',
    phone: '',
    location: '',
    site: '',
    department: '',
    notes: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfileData();
    fetchSitesAndDepartments();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const response = await api.getCurrentUser();

      if (!response.success || !response.data) {
        setMessage({
          type: 'error',
          text: response.message || 'Failed to load profile data',
        });
        return;
      }

      const user = response.data;
      setUserData(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        employeeId: user.employeeId || '',
        title:
          user.title ||
          (typeof user.jobType === 'object' && user.jobType?.name
            ? user.jobType.name
            : '') ||
          '',
        phone: user.phone || '',
        location: user.location || '',
        site: user.site?._id || user.site || '',
        department: user.department?._id || user.department || '',
        notes: user.notes || '',
      });

      // Stats are optional: users without asset list permission still get a profile.
      try {
        const userId = user._id || user.id;
        const assetsResponse = await api.getAssets(
          userId ? { assignedTo: userId } : {}
        );
        const assets = assetsResponse.data || [];

        const handoversResponse = await api.getHandovers();
        const handovers = handoversResponse.data || [];
        const myHandovers = handovers.filter(
          (h) =>
            h.assignedTo?._id === user._id ||
            h.assignedTo?.id === user._id ||
            h.assignedTo?._id === user.id ||
            h.assignedTo?.id === user.id
        );

        setStats({
          myAssets: assets.length,
          pendingAcknowledgements: myHandovers.filter(
            (h) => h.acknowledgementStatus === 'pending'
          ).length,
          acknowledgedAssets: myHandovers.filter(
            (h) => h.acknowledgementStatus === 'acknowledged'
          ).length,
        });
      } catch (statsError) {
        console.error('Error fetching profile stats (non-fatal):', statsError);
        setStats({
          myAssets: 0,
          pendingAcknowledgements: 0,
          acknowledgedAssets: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSitesAndDepartments = async () => {
    try {
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching sites and departments:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const userId = user.id || userData?._id || userData?.id;

      // Only update editable fields: name, email, phone
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined
      };

      const response = await api.updateUser(userId, updateData);

      if (response.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setEditing(false);
        await fetchProfileData();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await api.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (response.success) {
        toast.success('Password changed successfully!');
        
        // Reset password form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswords({ currentPassword: false, newPassword: false, confirmPassword: false });
        setShowPasswordChange(false);
        setMessage({ type: '', text: '' });
      } else {
        toast.error(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatRoleLabel = (role) => {
    if (!role) return 'N/A';
    const key = String(role).trim().toLowerCase().replace(/[\s_-]/g, '');
    if (key === 'teamleader') return 'Team Leader';
    if (key === 'hod') return 'HOD';
    if (key === 'admin') return 'Admin';
    if (key === 'employee') return 'Employee';
    return String(role).charAt(0).toUpperCase() + String(role).slice(1);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Message Alert */}
      {message.text && (
        <div 
          className="message-alert"
          style={{
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '16px',
            backgroundColor: message.type === 'success' ? '#d1fae5' : 
                           message.type === 'error' ? '#fee2e2' : '#dbeafe',
            color: message.type === 'success' ? '#065f46' : 
                   message.type === 'error' ? '#991b1b' : '#1e40af',
            border: `1px solid ${message.type === 'success' ? '#10b981' : 
                                   message.type === 'error' ? '#ef4444' : '#3b82f6'}`
          }}
        >
          {message.text}
        </div>
      )}

      {/* Editable Profile Information */}
      <div className="dashboard-card" style={{ marginBottom: '16px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: '16px' }}>Personal Information</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {!editing ? (
              <button 
                className="btn-primary"
                onClick={() => setEditing(true)}
                style={{ padding: '6px 16px', fontSize: '13px' }}
              >
                Edit
              </button>
            ) : (
              <>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setEditing(false);
                    fetchProfileData();
                  }}
                  disabled={saving}
                  style={{ padding: '6px 16px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleSaveProfile}
                  disabled={saving}
                  style={{ padding: '6px 16px', fontSize: '13px' }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          <form onSubmit={handleSaveProfile}>
            <div className="responsive-grid-2" style={{ gap: '16px', marginBottom: '0' }}>
              {/* Name */}
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  disabled={!editing}
                  style={{ padding: '8px 12px', fontSize: '13px' }}
                />
              </div>

              {/* Email */}
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  disabled={!editing}
                  style={{ padding: '8px 12px', fontSize: '13px' }}
                />
              </div>

              {/* Phone */}
              <div className="form-group" style={{ marginBottom: '0' }}>
                <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={!editing}
                  style={{ padding: '8px 12px', fontSize: '13px' }}
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Read-only Profile Information */}
      <div className="dashboard-card">
        <div className="card-header" style={{ padding: '12px 16px' }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: '16px' }}>Profile Information</h2>
        </div>
        <div className="card-body" style={{ padding: '16px' }}>
          <div className="responsive-grid-3" style={{ gap: '12px', marginBottom: '12px' }}>
            {/* Employee ID */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Employee ID</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {formData.employeeId || 'N/A'}
              </div>
            </div>

            {/* Title */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Job Title</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {formData.title || 'N/A'}
              </div>
            </div>

            {/* Location */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Location</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {formData.location || 'N/A'}
              </div>
            </div>

            {/* Site */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Site</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {sites.find(s => (s._id || s.id) === formData.site)?.name || 'N/A'}
              </div>
            </div>

            {/* Department */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Department</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                minHeight: '36px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {departments.find(d => (d._id || d.id) === formData.department)?.name || 'N/A'}
              </div>
            </div>
          </div>

          {/* Notes */}
          {formData.notes && (
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Notes</label>
              <div style={{ 
                padding: '8px 12px', 
                background: '#f7fafc', 
                borderRadius: '6px', 
                fontSize: '13px', 
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                whiteSpace: 'pre-wrap'
              }}>
                {formData.notes}
              </div>
            </div>
          )}

          {/* Account Info (Read-only) */}
          <div style={{
            padding: '12px',
            background: '#f7fafc',
            borderRadius: '6px',
            marginTop: '12px'
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#4a5568' }}>
              Account Information
            </h3>
            <div className="responsive-grid-3" style={{ gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '2px' }}>
                  Role
                </label>
                <div style={{ fontSize: '13px', color: '#1a202c', fontWeight: 500 }}>
                  {formatRoleLabel(userData?.role)}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '2px' }}>
                  Account Created
                </label>
                <div style={{ fontSize: '13px', color: '#1a202c', fontWeight: 500 }}>
                  {formatDate(userData?.createdAt)}
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '2px' }}>
                  Account Status
                </label>
                <div style={{ fontSize: '13px', color: '#1a202c', fontWeight: 500 }}>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: userData?.isActive ? '#d1fae5' : '#fee2e2',
                    color: userData?.isActive ? '#065f46' : '#991b1b'
                  }}>
                    {userData?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="dashboard-card" style={{ marginTop: '16px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
          <h2 className="card-title" style={{ margin: 0, fontSize: '16px' }}>Change Password</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {!showPasswordChange ? (
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowPasswordChange(true);
                }}
                style={{ padding: '6px 16px', fontSize: '13px' }}
              >
                Change Password
              </button>
            ) : (
              <>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setShowPasswords({ currentPassword: false, newPassword: false, confirmPassword: false });
                  }}
                  disabled={saving}
                  style={{ padding: '6px 16px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  form="change-password-form"
                  className="btn-primary"
                  disabled={saving}
                  style={{ padding: '6px 16px', fontSize: '13px' }}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </>
            )}
          </div>
        </div>
        {showPasswordChange && (
          <div className="card-body" style={{ padding: '16px' }}>
            <form id="change-password-form" onSubmit={handleChangePassword}>
              <div className="responsive-grid-2" style={{ gap: '16px', marginBottom: '12px' }}>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Current Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.currentPassword ? 'text' : 'password'}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="form-input"
                      required
                      style={{ padding: '8px 40px 8px 12px', fontSize: '13px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, currentPassword: !prev.currentPassword }))}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#718096',
                        transition: 'color 0.3s ease'
                      }}
                      aria-label={showPasswords.currentPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.currentPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div></div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>New Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.newPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="form-input"
                      required
                      minLength={6}
                      style={{ padding: '8px 40px 8px 12px', fontSize: '13px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, newPassword: !prev.newPassword }))}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#718096',
                        transition: 'color 0.3s ease'
                      }}
                      aria-label={showPasswords.newPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.newPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: '0' }}>
                  <label className="form-label" style={{ fontSize: '12px', marginBottom: '4px' }}>Confirm New Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.confirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="form-input"
                      required
                      minLength={6}
                      style={{ padding: '8px 40px 8px 12px', fontSize: '13px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#718096',
                        transition: 'color 0.3s ease'
                      }}
                      aria-label={showPasswords.confirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.confirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeProfilePage;
